import React, { useState, useEffect } from "react";
import {
  Stack,
  Text,
  Checkbox,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import {
  getCacheInstanceNames,
  clearAuthCache,
  clearCacheByNames,
} from "@/api/cache";

const CachingPage = () => {
  const [instanceNames, setInstanceNames] = useState<string[]>([]);
  const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    type: MessageBarType;
  } | null>(null);

  useEffect(() => {
    setIsLoading(true);
    getCacheInstanceNames()
      .then((names) => setInstanceNames(names))
      .catch(() =>
        setMessage({
          text: "Failed to load cache instance names.",
          type: MessageBarType.error,
        })
      )
      .finally(() => setIsLoading(false));
  }, []);

  const handleToggle = (name: string, checked?: boolean) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(name);
      } else {
        next.delete(name);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedNames.size === instanceNames.length) {
      setSelectedNames(new Set());
    } else {
      setSelectedNames(new Set(instanceNames));
    }
  };

  const handleClearSelected = async () => {
    if (selectedNames.size === 0) return;
    setIsClearing(true);
    setMessage(null);
    try {
      await clearCacheByNames(Array.from(selectedNames));
      setMessage({
        text: `Successfully cleared ${selectedNames.size} cache instance(s).`,
        type: MessageBarType.success,
      });
      setSelectedNames(new Set());
    } catch {
      setMessage({
        text: "Failed to clear selected cache instances.",
        type: MessageBarType.error,
      });
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearAll = async () => {
    setIsClearing(true);
    setMessage(null);
    try {
      await clearAuthCache();
      setMessage({
        text: "Successfully cleared all cache instances.",
        type: MessageBarType.success,
      });
      setSelectedNames(new Set());
    } catch {
      setMessage({
        text: "Failed to clear all cache instances.",
        type: MessageBarType.error,
      });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { padding: "16px" } }}>
      <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
        Caching
      </Text>

      {message && (
        <MessageBar
          messageBarType={message.type}
          onDismiss={() => setMessage(null)}
          dismissButtonAriaLabel="Close"
        >
          {message.text}
        </MessageBar>
      )}

      {isLoading ? (
        <Spinner size={SpinnerSize.medium} label="Loading cache instances..." />
      ) : (
        <>
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton
              text="Clear All"
              onClick={handleClearAll}
              disabled={isClearing || instanceNames.length === 0}
              iconProps={{ iconName: "Delete" }}
            />
            <DefaultButton
              text={`Clear Selected (${selectedNames.size})`}
              onClick={handleClearSelected}
              disabled={isClearing || selectedNames.size === 0}
              iconProps={{ iconName: "ClearSelection" }}
            />
            <DefaultButton
              text={
                selectedNames.size === instanceNames.length
                  ? "Deselect All"
                  : "Select All"
              }
              onClick={handleSelectAll}
              disabled={instanceNames.length === 0}
              iconProps={{ iconName: "CheckList" }}
            />
          </Stack>

          {isClearing && (
            <Spinner size={SpinnerSize.small} label="Clearing cache..." />
          )}

          <Stack
            tokens={{ childrenGap: 4 }}
            styles={{
              root: {
                border: "1px solid #e1e5e9",
                borderRadius: 4,
                padding: 12,
              },
            }}
          >
            <Text
              variant="mediumPlus"
              styles={{ root: { fontWeight: 600, marginBottom: 8 } }}
            >
              Cache Instances ({instanceNames.length})
            </Text>
            {instanceNames.length === 0 ? (
              <Text styles={{ root: { color: "#605e5c" } }}>
                No cache instances configured.
              </Text>
            ) : (
              instanceNames.map((name) => (
                <Checkbox
                  key={name}
                  label={name}
                  checked={selectedNames.has(name)}
                  onChange={(_, checked) => handleToggle(name, checked)}
                  styles={{ root: { padding: "6px 0" } }}
                />
              ))
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default CachingPage;
